// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import {
  PrimaryButton,
  PrimaryButtonLink,
  TrackedPrimaryButtonLink
} from 'components/Button';
import Box from 'components/Box';
import Icon, { AnimatedCheckmark } from 'components/Icon';
import { fileIdToUrl } from 'utils/s3file';
import type { ButtonLinkData } from 'api/cms';
import { getIn, stringify } from 'utils/functional';
import { isLoggedIn } from 'utils/currentUser';
import { trackClicked } from 'utils/analytics';
import type { ModalType } from 'containers/ModalRoot';
import type { CurrentUser } from 'api/currentUser';
import CmsSubscribeContainer from 'containers/CmsSubscribeContainer';
import { showModal as reduxShowModal } from '../../redux/main/actions/modal';

const validButton = sectionButton =>
  !(sectionButton.type === 'CMS_PAGE' && !sectionButton.data.pageTitle);

const mapStateToProps = ({ currentUser }) => ({ currentUser });

type Context = { onClick: Function, download: boolean, href: string };

type OwnProps = {|
  eventName: string,
  eventProperties: Object,
  sectionButton: ButtonLinkData,
  children: Context => React.Node
|};

type Props = {|
  ...OwnProps,
  showModal: (ModalType, Object) => void,
  currentUser: CurrentUser
|};

const DownloadPermissionsContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  { showModal: reduxShowModal }
)(
  ({
    currentUser,
    sectionButton,
    showModal,
    eventName,
    eventProperties,
    children
  }: Props) => {
    const allowDownload =
      getIn(sectionButton, ['data', 'permission']) === 'PUBLIC' ||
      isLoggedIn(currentUser);
    const href = allowDownload
      ? fileIdToUrl(stringify(sectionButton.data.s3FileId))
      : '#';
    const onClick = e => {
      if (!allowDownload) {
        e.preventDefault();
        e.stopPropagation();
        const redirectTo = `${window.location.pathname}${
          window.location.search
        }`;
        showModal('LOGIN', { redirectTo, manualLogin: true });
      } else {
        trackClicked(eventName, {
          ...eventProperties,
          is_download_button: true
        });
      }
    };
    return children({ onClick, href, download: true });
  }
);

const ButtonLink = ({
  sectionButton,
  eventProperties,
  blockId,
  index
}: {
  sectionButton: ButtonLinkData,
  eventProperties: Object,
  blockId?: string,
  index?: number
}) => {
  const link =
    sectionButton.type === 'S3_FILE' ? (
      <DownloadPermissionsContainer
        sectionButton={sectionButton}
        eventName="CMS Block"
        eventProperties={eventProperties}
      >
        {({ onClick, href, download }) => (
          <PrimaryButtonLink
            onClick={onClick}
            href={href}
            title={sectionButton.data.pageTitle}
            data-qa-id="cms-download-button-link"
            waitForHref
          >
            <Box display="flex" alignItems="center">
              {download && (
                <Icon name="download" width={16} height={18} display="block" />
              )}
              <Box ml="8px">
                {download ? `  ${sectionButton.text}` : sectionButton.text}
              </Box>
            </Box>
          </PrimaryButtonLink>
        )}
      </DownloadPermissionsContainer>
    ) : sectionButton.type === 'SUBSCRIBE' ? (
      <CmsSubscribeContainer
        blockId={blockId}
        index={index}
        redirectUrl={sectionButton.data.redirectUrl}
        render={({ subscribe, isSubscribed }) => (
          <PrimaryButton
            onClick={() => {
              subscribe();
              trackClicked('CMS Block', eventProperties);
            }}
            data-qa-id="cms-subscribe-button-link"
            disabled={isSubscribed}
          >
            {!isSubscribed && sectionButton.text}
            {isSubscribed && (
              <>
                <AnimatedCheckmark mr="8px" stroke="white" />
                {getText('Subscribed')}
              </>
            )}
          </PrimaryButton>
        )}
      />
    ) : (
      <TrackedPrimaryButtonLink
        href={sectionButton.data.url}
        title={sectionButton.data.pageTitle}
        eventName="CMS Block"
        eventProperties={{ ...eventProperties, is_download_button: false }}
        data-qa-id="cms-button-link"
      >
        <Box display="flex" alignItems="center">
          {sectionButton.text}
        </Box>
      </TrackedPrimaryButtonLink>
    );

  return validButton(sectionButton) && <Box mt={[24, null, 36]}>{link}</Box>;
};

export default ButtonLink;
